import request from "@/utils/request";

function touchHoldSidebarBadgeAfterOrderMutation(promise) {
  return Promise.resolve(promise).then(
    res => {
      import('@/utils/holdSidebarBadge')
        .then(m => m.refreshHoldSidebarBadge())
        .catch(() => {})
      return res
    },
    err => Promise.reject(err)
  )
}

/**
 * GET 查询序列化：数组用重复键名（check_in_date=a&check_in_date=b），避免 axios 默认生成
 * check_in_date[]=... 中的方括号被 Tomcat 拒绝（Invalid character in request target）。
 */
function serializeOrderListParams(params) {
  const parts = []
  const enc = encodeURIComponent
  Object.keys(params || {}).forEach((key) => {
    const val = params[key]
    if (val === undefined) return
    if (val === null) return
    if (Array.isArray(val)) {
      val.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          parts.push(enc(key) + '=' + enc(String(item)))
        }
      })
    } else if (typeof val === 'object') {
      return
    } else {
      parts.push(enc(key) + '=' + enc(String(val)))
    }
  })
  return parts.join('&')
}

export function normalizeOrderYes(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (v == null || v === "") return false;
  const s = String(v).toLowerCase();
  return s === "yes" || s === "1" || s === "true";
}

/** 订单列表多选筛选项：逗号拼接传给后端（兼容单选旧参数 client_status=foo） */
export function joinOrderListMultiSelectParam(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null
  const parts = arr
    .map(v => (v == null ? '' : String(v).trim()))
    .filter(v => v !== '')
  return parts.length ? parts.join(',') : null
}

/** session 恢复：client_status / supplier_status 单选迁移为多选数组 */
export function normalizeOrderListStatusMultiQuery(listQuery) {
  if (!listQuery || typeof listQuery !== 'object') return
  if (!Array.isArray(listQuery.client_statuses)) {
    if (listQuery.client_status != null && String(listQuery.client_status).trim() !== '') {
      listQuery.client_statuses = [String(listQuery.client_status).trim()]
    } else {
      listQuery.client_statuses = []
    }
  }
  delete listQuery.client_status
  if (!Array.isArray(listQuery.supplier_statuses)) {
    if (listQuery.supplier_status != null && String(listQuery.supplier_status).trim() !== '') {
      listQuery.supplier_statuses = [String(listQuery.supplier_status).trim()]
    } else {
      listQuery.supplier_statuses = []
    }
  }
  delete listQuery.supplier_status
}

/** buildFetchParams：多选状态 → 逗号串 query 参数 */
export function applyOrderListStatusMultiParams(params) {
  if (!params || typeof params !== 'object') return
  const clientParam = joinOrderListMultiSelectParam(params.client_statuses)
  if (clientParam) {
    params.client_status = clientParam
  } else {
    delete params.client_status
  }
  delete params.client_statuses
  const supplierParam = joinOrderListMultiSelectParam(params.supplier_statuses)
  if (supplierParam) {
    params.supplier_status = supplierParam
  } else {
    delete params.supplier_status
  }
  delete params.supplier_statuses
}

/** 列表筛选：签约人/运营人含停用角色账号；领单人原已含停用。filterOptions 另含 inactive_signers/operators/orderers 供下拉标「已停用」 */
export function fetchOrderFilterOptions() {
  return request({
    url: "/order/filterOptions",
    method: "get"
  });
}

/** 订单统计看板 */
export function fetchOrderStats(data) {
  return request({
    url: "/order/stats",
    method: "post",
    data: data || {}
  });
}

/** Hold 房单今日取消提醒数量（侧栏角标：Hold 房、非已售、非已取消、取消日期为今天） */
export function fetchHoldCancellationTodayCount() {
  return request({
    url: '/order/hold-cancellation-today-count',
    method: 'get'
  })
}

/** 订单列表（分页、筛选、汇总）；config 可传 { timeout } 等 axios 配置 */
export function fetchList(params, config) {
  const cfg = { ...(config || {}) }
  if (!cfg.paramsSerializer) {
    cfg.paramsSerializer = serializeOrderListParams
  }
  return request({
    url: "/order/list",
    method: "get",
    params,
    ...cfg
  });
}

export function createOrder(data) {
  return touchHoldSidebarBadgeAfterOrderMutation(
    request({
      url: "/order/create",
      method: "post",
      data
    })
  );
}

export function updateOrder(id, data) {
  return touchHoldSidebarBadgeAfterOrderMutation(
    request({
      url: `/order/update/${id}`,
      method: "post",
      data
    })
  );
}

export function bindHoldToNewOrder(holdOrderId, newOrderId) {
  return touchHoldSidebarBadgeAfterOrderMutation(
    request({
      url: '/order/bindHoldToNewOrder',
      method: 'post',
      data: {
        hold_order_id: holdOrderId,
        new_order_id: newOrderId
      }
    })
  );
}

/** 财务-请款申请（向财务发起付款依据，与业务/财务系统对接） */
export function submitFinancePaymentRequest(orderId, data) {
  return request({
    url: `/order/${orderId}/finance/payment-request`,
    method: "post",
    data: data || {}
  });
}

/** 财务-撤销请款（仅财务尚未处理时可撤销） */
export function cancelFinancePaymentRequest(orderId) {
  return request({
    url: `/order/${orderId}/finance/payment-request/cancel`,
    method: "post",
    data: {}
  });
}

/** 财务-标记请款已处理（财务侧回调；标记后不可撤销请款） */
export function markFinancePaymentHandled(orderId, handled = true) {
  return request({
    url: `/order/${orderId}/finance/payment-request/mark-handled`,
    method: "post",
    data: { handled }
  });
}

/** 补款下单：在原订订单下生成独立补款子单 */
export function createSupplementaryOrder(parentOrderId, data) {
  return request({
    url: `/order/${parentOrderId}/supplementary`,
    method: "post",
    data: data || {}
  });
}

export function deleteOrder(id) {
  return touchHoldSidebarBadgeAfterOrderMutation(
    request({
      url: `/order/delete/${id}`,
      method: "post"
    })
  );
}

export function getOrder(id) {
  return request({
    url: `/order/${id}`,
    method: "get"
  });
}

/** 订单详情「备注信息」保存（右侧四类；内部备注变更时上一版写入内部历史） */
export function saveOrderRemarks(orderId, data) {
  return request({
    url: `/order/${orderId}/remarks`,
    method: "post",
    data: data || {}
  });
}

/** 订单交班：写入内部历史；更新领单人；已锁单时更新锁单人（不改签约人/运营人） */
export function handoverOrder(orderId, data) {
  return request({
    url: `/order/${orderId}/handover`,
    method: "post",
    data: data || {}
  });
}

/** 订单操作日志（分页） */
export function fetchOrderOperationLogs(orderId, params) {
  return request({
    url: `/order/${orderId}/operation-log/list`,
    method: "get",
    params: params || {}
  });
}

/** 订单邮件记录列表（分页） */
export function fetchOrderMailRecordList(orderId, params) {
  return request({
    url: `/order/${orderId}/mail-record/list`,
    method: "get",
    params: params || {}
  });
}

/** 邮件中心分页列表 */
export function fetchMailRecordPageList(params) {
  return request({
    url: "/order/mail-record/page",
    method: "get",
    params: params || {}
  });
}

/** 订单邮件记录详情 */
export function getOrderMailRecordDetail(orderId, recordId) {
  return request({
    url: `/order/${orderId}/mail-record/${recordId}`,
    method: "get"
  });
}

/** 人工补发订单邮件 */
export function resendOrderMail(orderId, data) {
  return request({
    url: `/order/${orderId}/mail/send`,
    method: "post",
    data: data || {}
  });
}

/** 邮件中心：补发财务 VCC；可选 confirmVccEmail / ccVccEmail 与记录内主送、抄送合并追加 */
export function resendFinanceVccByRecordId(recordId, data) {
  return request({
    url: `/order/mail-record/${recordId}/resend-fin-vcc`,
    method: 'post',
    data: data || {}
  })
}

/** 邮件中心：补发酒店促销到期提醒；可选 confirmVccEmail / ccVccEmail 与记录主送、抄送合并追加 */
export function resendHotelPromotionRemindByRecordId(recordId, data) {
  return request({
    url: `/order/mail-record/${recordId}/resend-hotel-promo-remind`,
    method: 'post',
    data: data || {}
  })
}

/** 工单：按订单查询列表 */
export function listWorkOrders(orderId) {
  return request({
    url: "/workOrder/list",
    method: "get",
    params: { orderId }
  });
}

/** 工单：全局分页列表（仅查看） */
export function fetchWorkOrderPageList(params) {
  return request({
    url: "/workOrder/pageList",
    method: "get",
    params
  });
}

/** 工单：创建 */
export function createWorkOrder(data) {
  return request({
    url: "/workOrder/create",
    method: "post",
    data
  });
}

/** 工单：认领（待处理→处理中） */
export function claimWorkOrder(id) {
  return request({
    url: `/workOrder/${id}/claim`,
    method: "post"
  });
}

/** 工单：跟进（追加备注） */
export function followWorkOrder(id, data) {
  return request({
    url: `/workOrder/${id}/follow`,
    method: "post",
    data: data || {}
  });
}

/** 工单：追加诉求（创建子工单，不在全局列表展示） */
export function appendWorkOrderAppeal(id, data) {
  return request({
    url: `/workOrder/${id}/appendAppeal`,
    method: "post",
    data: data || {}
  });
}

/** 工单：查询主工单下的追加诉求列表 */
export function listWorkOrderAppendAppeals(id) {
  return request({
    url: `/workOrder/${id}/appendAppeals`,
    method: "get"
  });
}

/** 工单：完成（处理中→已关闭） */
export function completeWorkOrder(id, data) {
  return request({
    url: `/workOrder/${id}/complete`,
    method: "post",
    data: data || {}
  });
}

/** 工单：删除（仅管理员） */
export function deleteWorkOrder(id) {
  return request({
    url: `/workOrder/delete/${id}`,
    method: "post"
  });
}

/** 售后单：按订单查询列表 */
export function listAfterSales(orderId) {
  return request({
    url: "/afterSales/list",
    method: "get",
    params: { orderId }
  });
}

/** 售后单：全局分页列表（仅查看） */
export function fetchAfterSalesPageList(params) {
  return request({
    url: "/afterSales/pageList",
    method: "get",
    params,
    paramsSerializer: serializeOrderListParams
  });
}

/** 售后单：创建 */
export function createAfterSales(data) {
  return request({
    url: "/afterSales/create",
    method: "post",
    data
  });
}

/** 售后单：认领（待处理→处理中） */
export function claimAfterSales(id) {
  return request({
    url: `/afterSales/${id}/claim`,
    method: "post"
  });
}

/** 售后单：跟进（处理中：更新处理要点并追加备注） */
export function followAfterSales(id, data) {
  return request({
    url: `/afterSales/${id}/follow`,
    method: "post",
    data: data || {}
  });
}

/** 售后单：完成（处理中→已完成） */
export function completeAfterSales(id, data) {
  return request({
    url: `/afterSales/${id}/complete`,
    method: "post",
    data: data || {}
  });
}

/** 售后单：重新处理（已完成→处理中，仅管理员） */
export function reopenAfterSales(id) {
  return request({
    url: `/afterSales/${id}/reopen`,
    method: "post"
  });
}

/**
 * 售后单：提交财务（处理中 → WAIT_FINANCE_CREATE_OFFSET）
 * 请求体：{ afterSaleId, liabilityType: 'SUPPLIER'|'CUSTOMER', refundAmountOriginal, compensateAmountOriginal, refundMethod, remark }
 */
export function submitAfterSalesToFinance(id, data) {
  return request({
    url: `/afterSales/${id}/submitToFinance`,
    method: "post",
    data: data || {}
  });
}

/** 仅更新状态（可选扩展） */
export function updateOrderStatus(id, status) {
  return request({
    url: `/order/update/${id}`,
    method: "post",
    data: { status }
  });
}

/** 发送确认邮件（占位，待邮件服务对接） */
export function sendConfirmationEmail(orderId) {
  return Promise.resolve({
    data: { success: true, message: "邮件服务待对接", orderId }
  });
}

/** OP 上下班：当前状态（仅 OP 时 data.opRole 为 true） */
export function fetchOpDutyStatus() {
  return request({
    url: "/order/opDuty/status",
    method: "get"
  });
}

/** OP 上下班：切换接单；body `{ onDuty: boolean }` */
export function setOpDuty(onDuty) {
  return request({
    url: "/order/opDuty",
    method: "post",
    data: { onDuty: !!onDuty }
  });
}

/** 启用 OP 展示名列表（入站 OTA 直连补领单人；仅管理员或 OP） */
export function fetchOpAssignableDisplayNames() {
  return request({
    url: "/order/opDuty/assignableOpDisplayNames",
    method: "get"
  });
}

/** 入站 OTA 直连订单领取领单人（美团/悦途/道旅） */
export function claimOrderer(orderId, ordererName) {
  return request({
    url: `/order/${orderId}/claimOrderer`,
    method: "post",
    data: ordererName ? { orderer_name: ordererName } : {}
  });
}

/** 列表/抽屉单列「跟进/完成人」：处理中显示跟进责任人，已关闭/已取消显示完成人；不拆成两列 */
export function formatFollowFinishUser(row) {
  if (!row) return "—";
  const c = row.statusCode;
  const fin = row.finisherName != null && String(row.finisherName).trim() !== "" ? String(row.finisherName).trim() : "";
  const fol = row.followUserName != null && String(row.followUserName).trim() !== "" ? String(row.followUserName).trim() : "";
  const han = row.handler != null && String(row.handler).trim() !== "" ? String(row.handler).trim() : "";
  if (c === "closed" || c === "cancelled") return fin || han || "—";
  if (c === "processing") return fol || han || "—";
  return "—";
}



// WEBPACK FOOTER //
// ./src/api/hotelOrder.js